import { clone, indexBy, mergeDeepRight, last, equals, path, isEmpty } from "ramda";
import { isFieldConfigComposedList, isFieldConfigComposed, NON_INPUT_FIELDS, hasFields } from "./FieldConfig";
import { FIELD_TYPES } from "./FieldTypes";
import { isArrayElement, isBasicFieldElement, isObjectElement } from "./FormRenderView";
import { filterNullOrUndefinedDeep, isNullOrUndefinedDeep, isNullOrUndefined, isNumber, machineFriendly, mergeFormData, isEmptyDeep } from './utils/utils';
import { formatInferredValue } from "./FieldTypes.format";
import { normalizeValue } from "./FieldTypes.normalize";
import { mapInferredData } from "./FieldValidationResult";
import { validateFieldType } from "./FieldTypes.validation";
import { applyDefaultBindings, makeUniqueWithinComposedListGroup } from "./defaultBinding";
class EventHandler {
    updateListeners = {};
    on(eventName, listenerName, fn) {
        if (!this.updateListeners[eventName]) {
            this.updateListeners[eventName] = {};
        }
        if (this.updateListeners[eventName][listenerName]) {
            console.warn(`Update listener already exists for ${eventName}`);
        }
        this.updateListeners[eventName][listenerName] = fn;
    }
    fire(eventName, value, field) {
        if (this.updateListeners[eventName]) {
            if (field) {
                if (this.updateListeners[eventName][field]) {
                    this.updateListeners[eventName]?.[field]?.(value);
                }
            }
            else {
                Object.values(this.updateListeners[eventName]).forEach(fn => {
                    fn(value);
                });
            }
        }
    }
    remove(eventName, field) {
        if (this.updateListeners[eventName]) {
            delete this.updateListeners[eventName][field];
        }
    }
}
const TRANSFORM_REGISTRY = {
    machineFriendly: (changingField) => machineFriendly(changingField.value || ""),
    createUniqueKey: (changingField, ctx, targetField) => {
        const base = machineFriendly(changingField.value || "");
        return makeUniqueWithinComposedListGroup(targetField.fieldPath, base, ctx.getRenderViewMap());
    },
};
export class FluidForm {
    additionalConfigurations;
    sourceConfig;
    config;
    renderView;
    renderViewMap;
    fieldConfigByName;
    allFieldConfigs = [];
    currentField = "";
    onUpdateCallback = [];
    events = new EventHandler();
    submissionId;
    validationResults;
    constructor(config, initialData = {}, additionalConfigurations = {}) {
        this.additionalConfigurations = additionalConfigurations;
        this.setFormConfig(config, initialData);
    }
    setValidationResults(validationResults) {
        this.validationResults = validationResults;
        this.events.fire("onValidationResultsChanged", validationResults);
    }
    clearValidationResults() {
        this.validationResults = undefined;
        this.events.fire("onValidationResultsChanged", { status: "success", fieldValidations: {}, inferredData: {} });
    }
    clearForm() {
        this.setSubmissionData({});
        this.clearValidationResults();
    }
    getFieldValidationResult(fieldPath) {
        return this.validationResults?.fieldValidations?.[fieldPath];
    }
    getValidationResults() {
        return this.validationResults;
    }
    mergeValidationResults(partialValidationResults) {
        this.validationResults = mergeDeepRight(this.validationResults, partialValidationResults);
        this.events.fire("onValidationResultsChanged", partialValidationResults);
    }
    getCompletedFieldsCount() {
        let completedFields = 0;
        this.allFieldConfigs.forEach(field => {
            if (!NON_INPUT_FIELDS.includes(field.type)) {
                const fieldValue = this.getValue(field.name);
                completedFields += isNullOrUndefinedDeep(fieldValue) ? 0 : 1;
            }
        });
        return completedFields;
    }
    getSectionCompletedFieldsCount(sectionName) {
        let completedFields = 0;
        this.allFieldConfigs.forEach(field => {
            if (field.section === sectionName && !NON_INPUT_FIELDS.includes(field.type)) {
                const fieldValue = this.getValue(field.name);
                const validationType = this.getFieldValidationResult(field.name)?.type;
                if (!isNullOrUndefinedDeep(fieldValue) && (validationType === undefined || validationType === 'correct')) {
                    completedFields++;
                }
            }
        });
        return completedFields;
    }
    clearFieldValidationResult(fieldPath) {
        if (this.validationResults) {
            delete this.validationResults.fieldValidations[fieldPath];
        }
    }
    setFormConfig(config, initialData) {
        if (!initialData) {
            initialData = this.getFormSubmission();
        }
        config = {
            label: '',
            key: 'form_' + Date.now(),
            fields: [],
            ...config,
        };
        config = removeInvalidConfigFields(config);
        if (equals(this.config, config)) {
            return;
        }
        this.sourceConfig = config;
        this.config = clone(config);
        this.addSectionsToFieldConfig(this.config);
        this.processConfig(this.config.fields || [], "");
        let allFieldConfigs = this.flattenedFields(this.config.fields);
        this.allFieldConfigs = allFieldConfigs;
        this.fieldConfigByName = indexBy(f => f.name, allFieldConfigs);
        if (initialData) {
            this.setSubmissionData(initialData);
        }
    }
    getAdditionalConfigValue(configKey, fieldName) {
        return this.additionalConfigurations?.[configKey]?.[fieldName];
    }
    getAdditionalConfigValuePath(configKey, configPath) {
        return path(configPath.split("."), this.additionalConfigurations?.[configKey]);
    }
    setAdditionalConfigValue(configKey, value) {
        if (equals(this.additionalConfigurations[configKey], value)) {
            return;
        }
        this.additionalConfigurations[configKey] = value;
        this.events.fire("onConfigChange", { configKey, value });
    }
    mergeAdditionalConfigValue(configKey, value) {
        this.additionalConfigurations[configKey] = mergeFormData(this.additionalConfigurations[configKey], value);
        this.events.fire("onConfigChange", { configKey, value });
    }
    addSectionsToFieldConfig(config) {
        let sectionsStack = [];
        if (config.fields) {
            let lastSection = config.type === "composedList" ? (config.label || config.key) : "main";
            sectionsStack.push({ section: lastSection, level: 0 });
            for (let field of config.fields) {
                let currentSection = sectionsStack[sectionsStack.length - 1];
                field.section = currentSection.section;
                if (field.type === "section") {
                    if (!field.sectionLevel) {
                        field.sectionLevel = 1;
                    }
                    while (sectionsStack.length > 0 && last(sectionsStack)?.level >= field.sectionLevel) {
                        sectionsStack.pop();
                    }
                    field.section = last(sectionsStack)?.section || "";
                    sectionsStack.push({ section: field.key, level: field.sectionLevel });
                }
                if (field.type === "section") {
                    lastSection = field.key;
                }
                if (field.fields) {
                    this.addSectionsToFieldConfig(field);
                }
            }
        }
    }
    getFormKey() {
        return this.config.key;
    }
    getFormLabel() {
        return this.config.label;
    }
    onUpdated(fn) {
        this.onUpdateCallback.push(fn);
    }
    callUpdateCallback(data) {
        this.onUpdateCallback.forEach(fn => fn(data));
    }
    isEmpty() {
        return isNullOrUndefinedDeep(this.getFormSubmission());
    }
    updateRenderViewIndex(renderView) {
        if (!renderView) {
            renderView = this.renderView;
        }
        const traverse = (view, path = "") => {
            if (path) {
                view.fieldPath = `${path}`;
            }
            if (isObjectElement(view, this.fieldConfigByName)) {
                Object.values(view.value).forEach(childView => {
                    let newPath = path ? `${path}.${childView.key}` : childView.key;
                    traverse(childView, newPath);
                });
            }
            else if (isArrayElement(view, this.fieldConfigByName)) {
                view.value.forEach((childView, index) => {
                    traverse(childView, `${path}.${index}`);
                });
            }
        };
        traverse(renderView, "");
    }
    buildRenderViewMap(renderView) {
        if (!renderView) {
            renderView = this.renderView;
        }
        const fields = {};
        const traverse = (view) => {
            if (view.fieldPath) {
                fields[view.fieldPath] = view;
            }
            if (isObjectElement(view, this.fieldConfigByName)) {
                // Handle object case
                Object.values(view.value).forEach(childView => {
                    traverse(childView);
                });
            }
            else if (isArrayElement(view, this.fieldConfigByName)) {
                // Handle array case
                view.value.forEach(item => {
                    traverse(item);
                });
            }
        };
        traverse(renderView);
        return fields;
    }
    setFirstFieldCurrent() {
        let cur = Object.values(this.renderViewMap)[0];
        if (!this.canFocusField(cur.fieldPath)) {
            this.nextField(cur.fieldPath);
        }
        else {
            this.setCurrentField(cur.fieldPath);
        }
    }
    getFirstFocusableField() {
        for (let key in this.renderViewMap) {
            if (this.canFocusField(key)) {
                return key;
            }
        }
    }
    getCurrentFieldConfig() {
        if (this.currentField) {
            return this.fieldConfigByName[this.currentField];
        }
    }
    canFocusField(fieldName) {
        let renderView = this.renderViewMap[fieldName];
        if (!renderView) {
            return false;
        }
        let config = this.fieldConfigByName[renderView.configName];
        return renderView
            && renderView.active
            && !NON_INPUT_FIELDS.includes(config?.type);
    }
    isFocused(fieldName) {
        return this.currentField == fieldName;
    }
    ensureFocusedField() {
        if (!this.currentField) {
            this.setFirstFieldCurrent();
        }
    }
    setCurrentField(fieldName) {
        let config = this.fieldConfigByName[fieldName];
        if (this.canFocusField(fieldName)) {
            // if(fieldName != this.currentField) {
            //   this.unfocusField(this.currentField!);
            // }
            this.currentField = fieldName;
            this.renderViewMap[fieldName].valueAtFocusStart = this.renderViewMap[fieldName].value;
            this.callUpdateCallback({ fieldName, type: "onFocus" });
        }
        else {
            this.nextField(fieldName);
        }
    }
    lastOnBlurField;
    unfocusField(fieldName) {
        let initialValue = this.renderViewMap[fieldName]?.valueAtFocusStart;
        let finalValue = this.renderViewMap[fieldName]?.value;
        if (this.renderViewMap[fieldName]) {
            this.renderViewMap[fieldName].firstSession = false;
        }
        this.lastOnBlurField = fieldName;
        this.events.fire("onBlur", { fieldName, initialValue, finalValue });
        this.currentField = undefined;
        console.log("unfocusField", fieldName, this.currentField);
    }
    addTextToCurrentField(text, goNext = true) {
        if (!this.currentField)
            return;
        this.setValue(this.currentField, text);
        if (goNext) {
            //Go next
            this.nextField(this.currentField);
        }
        this.callUpdateCallback({ fieldName: this.currentField, type: "onChange", subType: "setValue", value: text });
    }
    getRenderView() {
        return { ...this.renderView };
    }
    getRenderViewMap() {
        return { ...this.renderViewMap };
    }
    getFieldsNotValidated() {
        return Object.keys(this.renderViewMap).filter(field => isNullOrUndefined(this.validationResults?.fieldValidations?.[field]));
    }
    normalizeKeyName(key) {
        return key.replace(/[^a-zA-Z0-9_]/g, '_');
    }
    applyCamelCase(key) {
        return key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    }
    evaluateCondition(field, renderView) {
        let condition = field.condition;
        if (!condition) {
            return true;
        }
        let data = {};
        for (let key in renderView) {
            let field = renderView[key];
            let fieldConfig = this.fieldConfigByName[field.configName];
            data[field.key] = normalizeValue(field.value, fieldConfig);
        }
        //Use secure eval with Function constructor
        try {
            let argumentNames = [...Object.keys(data).map(this.normalizeKeyName).map(this.applyCamelCase), "siblings"];
            let args = Object.keys(data).map(k => data[k]);
            args.push(data);
            let result = new Function(...argumentNames, `return ${condition}`)(...args);
            return result;
        }
        catch (e) {
            console.error("Error evaluating condition", e);
            return false;
        }
    }
    processConfig(fields, parent) {
        fields.forEach((field, i) => {
            field.parent = parent;
            field.name = parent ? `${parent}.${field.key}` : field.key;
            if (isFieldConfigComposedList(field) || isFieldConfigComposed(field)) {
                this.processConfig(field.fields || [], field.name);
            }
        });
    }
    nextField(fieldName) {
        if (!fieldName) {
            fieldName = this.currentField;
        }
        if (!fieldName) {
            return undefined;
        }
        let allRenderViewList = Object.values(this.renderViewMap);
        let curIndex = allRenderViewList.findIndex(f => f.fieldPath === fieldName);
        while (curIndex < allRenderViewList.length - 1) {
            curIndex++;
            let field = allRenderViewList[curIndex];
            let type = this.fieldConfigByName[field.configName].type;
            if (!NON_INPUT_FIELDS.includes(type)) {
                this.setCurrentField(field.fieldPath);
                return field.fieldPath;
            }
        }
    }
    getPreviousField(fieldName) {
        if (!fieldName) {
            fieldName = this.currentField;
        }
        if (!fieldName) {
            return undefined;
        }
        let allRenderViewList = Object.values(this.renderViewMap);
        let curIndex = allRenderViewList.findIndex(f => f.fieldPath === fieldName);
        while (curIndex > 0) {
            curIndex--;
            let field = allRenderViewList[curIndex];
            let type = this.fieldConfigByName[field.configName].type;
            if (this.canFocusField(field.fieldPath)) {
                this.setCurrentField(field.fieldPath);
                return field.fieldPath;
            }
        }
        //If we are at the first field stay
        this.setCurrentField(fieldName);
    }
    flattenedFields(fields) {
        return fields.flatMap((field) => {
            if (isFieldConfigComposedList(field) || isFieldConfigComposed(field)) {
                return [field, ...this.flattenedFields(field.fields || [])];
            }
            else {
                return field;
            }
        });
    }
    swapComposed(fieldPath, fromIndex, toIndex) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        let items = fieldRenderView?.value;
        let item = items[fromIndex];
        items[fromIndex] = items[toIndex];
        items[toIndex] = item;
        this.callUpdateCallback({ fieldName: fieldPath, type: "onChange", subType: "swap", value: { fromIndex, toIndex } });
    }
    triggerFieldValueChanged(fieldPath, value) {
        this.clearFieldValidationResult(fieldPath);
        this.updateDefaultBindings(this.getFieldRenderView(fieldPath));
        this.callUpdateCallback({ fieldName: fieldPath, type: "onChange", subType: "setValue", value: value });
    }
    setValue(fieldPath, value, updateStack = new Set()) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView && this.isSimpleField(fieldRenderView)) {
            if (isNullOrUndefined(fieldRenderView.value)) {
                fieldRenderView.firstSession = true;
            }
            if (!equals(value, fieldRenderView.value)) {
                fieldRenderView.value = value;
                this.triggerFieldValueChanged(fieldPath, value);
            }
        }
        if (fieldRenderView && fieldRenderView.type === "composed") {
            let fieldConfig = this.fieldConfigByName[fieldRenderView.configName];
            fieldRenderView.value = this.builtRenderViewRec(fieldConfig.fields, fieldPath, [], value);
            this.triggerFieldValueChanged(fieldPath, value);
        }
        if (this.isComposedItem(fieldPath)) {
            this.setValueInComposed(fieldPath, value);
        }
        this.updateConditionals();
    }
    setFieldProperty(fieldPath, property, value) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView) {
            fieldRenderView[property] = value;
        }
        this.callUpdateCallback({ fieldName: fieldPath, type: "onChange", subType: "setProperty", property, value });
    }
    getFieldProperty(fieldPath, property) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView) {
            return fieldRenderView[property];
        }
        return undefined;
    }
    isSimpleField(fieldConfig) {
        return fieldConfig.type !== "composedList"
            && fieldConfig.type !== "form"
            && fieldConfig.type !== "composed-item"
            && fieldConfig.type !== "composed";
    }
    setValueInComposed(fieldPath, value) {
        let parentRenderView = this.getParentRenderView(fieldPath);
        if (!parentRenderView) {
            console.error(`Parent render view not found for field ${fieldPath}`);
            return;
        }
        let index = this.getIndexFromPath(fieldPath);
        let items = parentRenderView.value;
        if (index > items.length) {
            console.error(`Index ${index} is out of bounds for field ${fieldPath} with ${items.length} elements`);
            return;
        }
        if (index == items.length) {
            this.insertToComposed(parentRenderView.fieldPath, index);
        }
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView?.type === "composed-item") {
            //Update all fields in the composed
            let elements = fieldRenderView?.value || {};
            Object.entries(elements).forEach(([key, element]) => {
                if (!isNullOrUndefined(value[key])) {
                    this.setValue(element.fieldPath, value[key]);
                }
            });
        }
        else {
            this.setValue(fieldPath, value);
        }
    }
    getIndexFromPath(fieldPath) {
        let parts = fieldPath.split(".");
        if (parts.length == 2) {
            return 0;
        }
        let val = parseInt(parts[parts.length - 2]);
        if (!isNaN(val)) {
            return val;
        }
        return -1;
    }
    appendValue(fieldPath, value) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (typeof fieldRenderView?.value != "string") {
            console.error(`Field ${fieldPath} is not a string`);
            return;
        }
        if (fieldRenderView) {
            fieldRenderView.value = fieldRenderView.value + value;
            this.callUpdateCallback({ fieldName: fieldPath, type: "onChange", subType: "append", value: value });
            this.updateConditionals();
        }
    }
    fieldExists(fieldPath) {
        return this.getFieldRenderView(fieldPath) !== undefined;
    }
    getValue(fieldPath) {
        if (!fieldPath) {
            return "";
        }
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView?.type === "composedList") {
            return this.getFormSubmissionRecComposed(fieldRenderView);
        }
        if (fieldRenderView?.type === "composed-item") {
            return this.getFormSubmissionRec(fieldRenderView);
        }
        if (fieldRenderView?.type === "composed") {
            return this.getFormSubmissionRec(fieldRenderView);
        }
        return fieldRenderView?.value;
    }
    getPlainValue(fieldPath) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView && isBasicFieldElement(fieldRenderView, this.fieldConfigByName)) {
            return formatInferredValue(fieldRenderView.value, this.fieldConfigByName[fieldRenderView.configName]);
        }
        return fieldRenderView?.value;
    }
    getFieldRenderView(fieldPath) {
        let fieldRenderView = this.renderViewMap[fieldPath];
        return fieldRenderView;
    }
    isComposedItem(fieldPath) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView?.type === "composed-item") {
            return true;
        }
        if (!fieldRenderView && this.getIndexFromPath(fieldPath) != -1) {
            let parts = fieldPath.split(".");
            parts.pop();
            if (isNumber(parts[parts.length - 1])) {
                parts.pop();
            }
            let field = this.renderViewMap[parts.join(".")];
            return field?.type === "composedList";
        }
    }
    getParentRenderView(fieldPath) {
        let parts = fieldPath.split(".");
        parts.pop();
        //If the last part is a number, pop it
        if (isNumber(parts[parts.length - 1])) {
            parts.pop();
        }
        return this.renderViewMap[parts.join(".")];
    }
    getFieldConfigByFieldPath(fieldPath) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView) {
            return this.fieldConfigByName[fieldRenderView.configName];
        }
    }
    getConfig(name) {
        if (!name) {
            return this.config;
        }
        return this.fieldConfigByName[name];
    }
    getFieldSection(fieldPath) {
        let fieldConfig = this.getFieldConfigByFieldPath(fieldPath);
        if (fieldConfig?.section) {
            return this.getConfig(fieldConfig.section) || this.sourceConfig;
        }
        return this.sourceConfig;
    }
    //Get the paths of the sections recursively {sectionPath: sectionName}
    getSectionsPath(fieldPath) {
        if (!fieldPath) {
            return {};
        }
        let fieldConfig = this.getFieldConfigByFieldPath(fieldPath);
        if (fieldConfig?.section) {
            let sectionConfig = this.getConfig(fieldConfig.section);
            return {
                [fieldPath]: sectionConfig,
                ...this.getSectionsPath(fieldConfig.section)
            };
        }
        return {};
    }
    getConfigMap() {
        return this.fieldConfigByName;
    }
    getFormConfig() {
        return this.config;
    }
    getFieldKeys() {
        return this.allFieldConfigs.map(f => f.key);
    }
    //Filter fields in render view based on condition
    updateConditionals(field) {
        field = field ? field : this.renderView;
        if (isArrayElement(field, this.fieldConfigByName)) {
            field.value.forEach(f => {
                this.updateConditionals(f);
            });
        }
        else if (isObjectElement(field, this.fieldConfigByName)) {
            let siblings = field.value;
            Object.values(siblings).forEach(f => {
                let fieldConfig = this.getConfig(f.configName);
                f.active = this.evaluateCondition(fieldConfig, siblings);
                this.updateConditionals(f);
            });
        }
    }
    areSiblings(field1, field2) {
        let path1 = field1.fieldPath.split(".");
        let path2 = field2.fieldPath.split(".");
        path1.pop();
        path2.pop();
        return path1?.join(".") == path2?.join(".");
    }
    updateDefaultBindings(changingField) {
        applyDefaultBindings(this, changingField, TRANSFORM_REGISTRY);
    }
    //Todo: Make private
    buildRenderView(data, options, updatedFields = []) {
        return {
            key: this.config.key,
            fieldPath: "",
            configName: "",
            type: "form",
            value: this.builtRenderViewRec(this.config.fields || [], "", updatedFields, data, options),
            active: true
        };
    }
    builtRenderViewRec(fields, parent, updatedFields, data, options = {
        useStructuredData: false,
        useExamples: false
    }) {
        let renderView = {};
        for (let field of fields) {
            let fieldDef = FIELD_TYPES[field.type];
            let fieldPath = parent ? `${parent}.${field.key}` : field.key;
            if (!fieldDef) {
                console.error(`Field type ${field.type} not found, field: ${field.key}`);
                fieldDef = FIELD_TYPES["text"];
            }
            if (isFieldConfigComposedList(field)) {
                let value = options.useExamples ? [{
                        key: field.key,
                        fieldPath: `${fieldPath}.0`,
                        configName: field.name,
                        value: this.builtRenderViewRec(field.fields || [], `${fieldPath}.0`, updatedFields, data, options),
                        active: true,
                        type: "composed-item"
                    }] : [];
                if (!(data?.[field.key]) && field.defaultValue) {
                    data[field.key] = field.defaultValue;
                }
                if (data && Array.isArray(data[field.key])) {
                    value = data[field.key].map((d, i) => {
                        let fields = field.fields || [];
                        let item = this.builtRenderViewRec(fields, `${fieldPath}.${i}`, updatedFields, d, options);
                        return {
                            key: field.key,
                            fieldPath: `${fieldPath}.${i}`,
                            configName: field.name,
                            value: item,
                            active: true,
                            type: "composed-item"
                        };
                    });
                }
                else if (data && data[field.key] && !Array.isArray(data[field.key])) {
                    console.error(`Inconsistent type for composed field ${field.key} ${typeof data[field.key]}`);
                }
                renderView[field.key] = {
                    configName: field.name,
                    value: value,
                    fieldPath: fieldPath,
                    key: field.key,
                    active: true,
                    type: field.type
                };
            }
            else if (isFieldConfigComposed(field)) {
                if (!(data?.[field.key]) && field.defaultValue) {
                    data[field.key] = field.defaultValue;
                }
                renderView[field.key] = {
                    configName: field.name,
                    value: this.builtRenderViewRec(field.fields || [], fieldPath, updatedFields, data?.[field.key], options),
                    fieldPath: fieldPath,
                    key: field.key,
                    active: true,
                    type: field.type
                };
            }
            else if (field.type === "section") {
                renderView[field.key] = {
                    configName: field.name,
                    value: "--",
                    fieldPath: fieldPath,
                    key: field.key,
                    active: true,
                    type: field.type
                };
            }
            else {
                let value = options.useExamples ? fieldDef.examples[0] : field.defaultValue;
                if (options.useStructuredData) {
                    value = fieldDef.structuredExamples?.[0] || fieldDef.examples[0] || field.defaultValue;
                }
                if (data && !isNullOrUndefined(data?.[field.key])) {
                    value = data[field.key];
                }
                let previousValue = this.renderViewMap?.[fieldPath]?.value;
                if (previousValue && !equals(previousValue, value)) {
                    console.info("value changing", fieldPath, previousValue, value);
                    updatedFields.push(fieldPath);
                }
                renderView[field.key] = {
                    configName: field.name,
                    value: value,
                    fieldPath: fieldPath,
                    key: field.key,
                    active: true,
                    type: field.type,
                    previousValue
                };
            }
        }
        return renderView;
    }
    insertToComposed(fieldPath, index = -1) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView && Array.isArray(fieldRenderView.value)) {
            let fieldConfig = this.fieldConfigByName[fieldRenderView.configName];
            if (!isFieldConfigComposedList(fieldConfig)) {
                return -1;
            }
            if (index == -1) {
                index = fieldRenderView.value.length;
            }
            let newFieldPath = `${fieldPath}.${index}`;
            fieldRenderView.value.splice(index, 0, {
                key: fieldConfig.key,
                fieldPath: newFieldPath,
                configName: fieldConfig.name,
                value: this.builtRenderViewRec(fieldConfig.fields || [], newFieldPath, []),
                active: true,
                type: "composed-item"
            });
            this.setRenderView(this.renderView);
            this.updateConditionals();
            return index;
        }
        return -1;
    }
    moveComposed(fieldPath, fromIndex, toIndex) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView && Array.isArray(fieldRenderView.value)) {
            let tmp = fieldRenderView.value[fromIndex];
            fieldRenderView.value[fromIndex] = fieldRenderView.value[toIndex];
            fieldRenderView.value[toIndex] = tmp;
            this.callUpdateCallback({ fieldName: fieldPath, type: "onChange", subType: "move", value: { fromIndex, toIndex } });
        }
    }
    deleteFromComposed(fieldPath, index) {
        let fieldRenderView = this.getFieldRenderView(fieldPath);
        if (fieldRenderView && Array.isArray(fieldRenderView.value)) {
            let fieldConfig = this.fieldConfigByName[fieldRenderView.configName];
            if (!isFieldConfigComposedList(fieldConfig)) {
                throw new Error("Field is not a composed");
            }
            fieldRenderView.value.splice(index, 1);
            delete this.renderViewMap[`${fieldPath}.${index}`];
            this.callUpdateCallback({ fieldName: fieldPath, type: "onChange", subType: "delete", value: index });
        }
    }
    setSubmissionData(formData) {
        let updates = [];
        this.setRenderView(this.buildRenderView(formData, {}, updates));
        for (let update of updates) {
            this.triggerFieldValueChanged(update, this.renderViewMap[update]?.value);
        }
    }
    mergeSubmissionData(formData, treatEmptyAsNull = false) {
        let current = this.getFormSubmission();
        let merged = mergeFormData(current, formData, treatEmptyAsNull);
        this.setSubmissionData(merged);
    }
    setRenderView(renderView) {
        this.renderView = renderView;
        this.updateRenderViewIndex();
        this.renderViewMap = this.buildRenderViewMap();
        this.callUpdateCallback({ type: "onRenderViewChange", value: this.renderView });
        this.updateConditionals();
    }
    getFormSubmission() {
        return filterNullOrUndefinedDeep(this.getFormSubmissionRec(this.renderView)) || {};
    }
    getFormSubmissionFlat() {
        let renderViewMap = this.getRenderViewMap();
        let result = {};
        for (let [key, value] of Object.entries(renderViewMap)) {
            result[key] = value.value;
        }
        return result;
    }
    getFormSubmissionExample({ useStructuredData } = {}) {
        return this.getFormSubmissionRec(this.buildRenderView({}, { useExamples: true, useStructuredData }));
    }
    buildSubForm(fieldsToInclude) {
        let configCopy = filterNullOrUndefinedDeep(this.config);
        filterFields(configCopy, fieldsToInclude);
        let form = new FluidForm(configCopy, this.getFormSubmission());
        return form;
    }
    getFormSubmissionRecComposed(value) {
        // Handle composed fields (arrays of objects)
        if (Array.isArray(value.value)) {
            return value.value.map((item) => this.getFormSubmissionRec(item));
        }
        else {
            return [];
        }
    }
    getFormSubmissionRec(field) {
        const result = {};
        let fields = field.value || field;
        for (const [key, value] of Object.entries(fields)) {
            if (isEmpty(key)) {
                continue;
            }
            const fieldConfig = this.fieldConfigByName[value.configName];
            if (!fieldConfig) {
                console.warn(`Field config not found for key: ${key}`);
                continue;
            }
            if (isFieldConfigComposedList(fieldConfig)) {
                // Handle composed fields (arrays of objects)
                result[fieldConfig.key] = this.getFormSubmissionRecComposed(value);
            }
            else if (isFieldConfigComposed(fieldConfig)) {
                result[fieldConfig.key] = this.getFormSubmissionRec(value);
            }
            else if (fieldConfig.type === "section") {
                // Skip section fields in submission data
                continue;
            }
            else {
                // Handle basic fields
                result[fieldConfig.key] = value.value;
            }
        }
        return result;
    }
    getChangedFields(other) {
        let otherFormSubmission = new FluidForm(this.sourceConfig, other);
        let otherFormViewMap = otherFormSubmission.getRenderViewMap();
        let thisFormViewMap = this.getRenderViewMap();
        //Right changes
        let changedFields = new Set();
        for (let [key, field] of Object.entries(otherFormViewMap)) {
            if (!isBasicFieldElement(field, this.fieldConfigByName)) {
                continue;
            }
            if (!equals(field.value, thisFormViewMap[key]?.value)) {
                changedFields.add(field.fieldPath);
            }
        }
        //Left changes
        for (let [key, field] of Object.entries(thisFormViewMap)) {
            if (!isBasicFieldElement(field, this.fieldConfigByName)) {
                continue;
            }
            if (!equals(field.value, otherFormViewMap[key]?.value)) {
                changedFields.add(field.fieldPath);
            }
        }
        return Array.from(changedFields);
    }
    preValidate() {
        let fieldValidationResults = {};
        let form = this;
        let fieldConfigs = form.getConfigMap();
        for (let fieldConfig of Object.values(fieldConfigs)) {
            //Todo: We have to support composed lists minItems and maxItems
            if (fieldConfig.type === "section" || fieldConfig.type === "composedList") {
                continue;
            }
            let fieldPath = fieldConfig.name;
            let value = form.getValue(fieldPath);
            //Check all empty non required fields and mark them as correct
            if (!fieldConfig.required && isEmptyDeep(value)) {
                fieldValidationResults[fieldPath] = {
                    fieldPath: fieldPath,
                    type: "correct",
                    question: ``,
                };
            }
            //Check all empty required fields and mark them as retry
            if (fieldConfig.required && isEmptyDeep(value)) {
                fieldValidationResults[fieldPath] = {
                    fieldPath: fieldPath,
                    type: "retry",
                    question: `Field '${fieldConfig.label || fieldConfig.key}' is required but was not provided. Please enter the value.`,
                };
            }
        }
        let status = Object.values(fieldValidationResults)
            .every((validation) => validation.type === "correct") ? "success" : "error";
        return {
            status: status,
            fieldValidations: fieldValidationResults,
            inferredData: mapInferredData(this, fieldValidationResults)
        };
    }
    isInputField(field) {
        return !NON_INPUT_FIELDS.includes(this.fieldConfigByName[field.configName].type);
    }
    validate() {
        let viewMap = this.getRenderViewMap();
        let validationResults = {
            status: "success",
            fieldValidations: {},
        };
        validationResults = this.preValidate();
        for (let [key, field] of Object.entries(viewMap)) {
            if (!this.isInputField(field)) {
                continue;
            }
            if (validationResults.fieldValidations[field.fieldPath]) {
                continue;
            }
            if (!isBasicFieldElement(field, this.fieldConfigByName)) {
                continue;
            }
            let fieldConfig = this.fieldConfigByName[field.configName];
            let fieldResult = validateFieldType(fieldConfig, field.fieldPath, field.value);
            if (fieldResult.type !== "correct") {
                validationResults.status = "error";
            }
            validationResults.fieldValidations[field.fieldPath] = fieldResult;
        }
        this.validationResults = validationResults;
        validationResults.inferredData = mapInferredData(this, validationResults.fieldValidations);
        return validationResults;
    }
}
//Removes invalid fields recursively
export function removeInvalidConfigFields(formConfig) {
    formConfig.fields = formConfig.fields?.filter((field) => {
        if (!field.type || !FIELD_TYPES[field.type]) {
            console.warn("Invalid field type", field);
            return false;
        }
        if (!field.key) {
            console.warn("Field type is missing key", field);
            return false;
        }
        if (field.type === "composed" || field.type === "composedList") {
            if (!field.fields) {
                field.fields = [];
            }
            return removeInvalidConfigFields(field);
        }
        return true;
    });
    return formConfig;
}
//Recursively filter out fields from the config
function filterFields(config, includeFields = []) {
    config.fields = config.fields.filter(f => includeFields.includes(f.name));
    for (let field of config.fields) {
        if (hasFields(field)) {
            filterFields(field, includeFields);
        }
    }
}
